import axios from "axios";
import { handleMockRequest } from "./mockServer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";
const USE_MOCK = (process.env.REACT_APP_USE_MOCK || "false").toLowerCase() === "true";

const TOKEN_KEY = "bms_token";
const USER_KEY = "bms_user";
const SESSION_KEY = "bms_session";
const ACTIVE_SESSION_KEY = "bms_active_session_id";
const pendingRequests = new Set();
export const cancelPendingRequests = () => {
  pendingRequests.forEach((controller) => controller.abort());
  pendingRequests.clear();
};

const readSession = () => {
  try {
    const value = localStorage.getItem(SESSION_KEY);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

export const activateStoredSession = (session = readSession()) => {
  if (session?.sessionId) sessionStorage.setItem(ACTIVE_SESSION_KEY, session.sessionId);
  else sessionStorage.removeItem(ACTIVE_SESSION_KEY);
};
export const getStoredSession = () => readSession();
export const isActiveStoredSession = () => {
  const session = readSession();
  const activeSessionId = sessionStorage.getItem(ACTIVE_SESSION_KEY);
  return session ? activeSessionId === session.sessionId : !activeSessionId;
};

export const getStoredToken = () => readSession()?.token || localStorage.getItem(TOKEN_KEY);
export const getStoredUser = () => {
  const session = readSession();
  if (session) return session.user || null;
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};
export const setStoredSession = (token, user) => {
  // One storage write gives every tab a consistent token and role snapshot.
  cancelPendingRequests();
  const session = { token, user, sessionId: `${Date.now()}-${Math.random()}` };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  activateStoredSession(session);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};
export const clearStoredSession = () => {
  cancelPendingRequests();
  localStorage.removeItem(SESSION_KEY);
  activateStoredSession(null);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};
export const setStoredToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearStoredToken = () => localStorage.removeItem(TOKEN_KEY);

export const setStoredUser = (u) => localStorage.setItem(USER_KEY, JSON.stringify(u));
export const clearStoredUser = () => localStorage.removeItem(USER_KEY);

// Mock adapter: short-circuits axios to use the in-memory mock server.
const mockAdapter = (config) => new Promise((resolve, reject) => {
  setTimeout(() => {
    try {
      const url = (config.url || "").replace(config.baseURL || "", "");
      const result = handleMockRequest({
        method: config.method,
        url,
        data: typeof config.data === "string" ? JSON.parse(config.data || "{}") : (config.data || {}),
        params: config.params,
      });
      if (result.status >= 400) {
        const err = new Error(result.data?.message || "Mock error");
        err.response = result;
        return reject(err);
      }
      resolve({
        ...result,
        statusText: "OK",
        headers: {},
        config,
        request: {},
      });
    } catch (e) {
      reject(e);
    }
  }, 120);
});

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
  adapter: USE_MOCK ? mockAdapter : undefined,
});

axiosClient.interceptors.request.use((config) => {
  const isLoginRequest = config.url?.includes("/api/auth/login");
  if (!isLoginRequest && !isActiveStoredSession()) {
    return Promise.reject(new axios.CanceledError("Waiting for this tab to synchronize the authentication session"));
  }
  const token = getStoredToken();
  config.__authToken = token;
  const controller = new AbortController();
  const upstreamSignal = config.signal;
  const relayAbort = () => controller.abort(upstreamSignal?.reason);
  if (upstreamSignal?.aborted) relayAbort();
  else upstreamSignal?.addEventListener("abort", relayAbort, { once: true });
  config.__sessionController = controller;
  config.__upstreamSignal = upstreamSignal;
  config.__relayAbort = relayAbort;
  config.signal = controller.signal;
  pendingRequests.add(controller);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let onAuthFailure;
export const setAuthFailureHandler = (fn) => { onAuthFailure = fn; };

axiosClient.interceptors.response.use(
  (res) => {
    pendingRequests.delete(res.config.__sessionController);
    res.config.__upstreamSignal?.removeEventListener("abort", res.config.__relayAbort);
    if (res.config.__authToken !== getStoredToken()) {
      return Promise.reject(new axios.CanceledError("Authentication session changed"));
    }
    return res;
  },
  (error) => {
    pendingRequests.delete(error.config?.__sessionController);
    error.config?.__upstreamSignal?.removeEventListener("abort", error.config.__relayAbort);
    if (error.config && error.config.__authToken !== getStoredToken()) {
      return Promise.reject(new axios.CanceledError("Authentication session changed"));
    }
    const isBackendDown =
      (error.code !== "ERR_CANCELED" && !error.response) ||
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error" ||
      error.code === "ECONNABORTED";

    if (
      !USE_MOCK &&
      !error.config?.skipGlobalErrorRedirect &&
      isBackendDown &&
      window.location.pathname !== "/backend-down"
    ) {
      window.location.href = "/backend-down";
      return Promise.reject(error);
    }

    const status = error?.response?.status;

    // A late 401 from a request made before an account switch must not log
    // out the newly authenticated account.
    if (status === 401 && error.config?.__authToken === getStoredToken()) {
      clearStoredSession();
      if (onAuthFailure) onAuthFailure();
    }

    return Promise.reject(error);
  },
);

// Helper: most backend handlers return {success, message, data}.
// Unwrap to just data for service-layer convenience.
export const unwrap = (res) => {
  const body = res?.data;
  if (body && typeof body === "object" && "data" in body) return body.data;
  return body;
};

export { API_BASE_URL, USE_MOCK };
export default axiosClient;
